import { useSession } from '@/auth/useSession';
import { useTimeZone } from '@/auth/useTimeZone';
import { LoadingPage } from '@/components/loading-page';
import { EmptyArray, getMyStudentSessions } from '@/lib/api-calls';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Temporal } from 'temporal-polyfill';
import { SessionSection } from './session-section';
import { useErrorLogger } from '@/hooks/use-error-logger';

export const StudentHome = () => {
  const { actingUser } = useSession();
  const { timeZone } = useTimeZone();
  const logError = useErrorLogger();

  const todayDate = Temporal.Now.zonedDateTimeISO(timeZone).toPlainDate();

  const dayStartInstant = (d: Temporal.PlainDate, tz: string) =>
    d.toZonedDateTime({ timeZone: tz, plainTime: Temporal.PlainTime.from('00:00') }).toInstant();

  const startOfToday = dayStartInstant(todayDate, timeZone);
  const startOfTomorrow = dayStartInstant(todayDate.add({ days: 1 }), timeZone);

  const { data, isPending, error } = useQuery(
    getMyStudentSessions({
      pageIndex: '0',
      pageSize: '20',
      startOnOrAfter: startOfToday.toString(),
    }),
  );

  useEffect(() => {
    if (error) {
      logError('student session view fetch failed', {
        component: 'StudentHome',
        action: 'fetch_failed',
        userId: actingUser?.id,
      });
    }
  }, [error, logError, actingUser?.id]);

  const sessions = data?.studentSessions ?? EmptyArray;

  const toLocalDate = (inst: Temporal.Instant) => inst.toZonedDateTimeISO(timeZone).toPlainDate();
  const sorted = [...sessions].sort((a, b) => Temporal.Instant.compare(a.startInstant, b.startInstant));

  // Upcoming = all sessions happening today (00:00–23:59 local)
  const sessionsToday = sorted.filter(
    (s) =>
      Temporal.Instant.compare(s.startInstant, startOfToday) >= 0 &&
      Temporal.Instant.compare(s.startInstant, startOfTomorrow) < 0,
  );

  let upcoming = sessionsToday;

  // If none today, take all sessions on the next day that has sessions
  if (upcoming.length === 0) {
    const firstAfterToday = sorted.find((s) => Temporal.Instant.compare(s.startInstant, startOfTomorrow) >= 0);
    upcoming = firstAfterToday
      ? sorted.filter((s) => toLocalDate(s.startInstant).equals(toLocalDate(firstAfterToday.startInstant)))
      : [];
  }

  // Future = everything from start of today forward (includes Upcoming)
  const futures = sorted.filter((s) => Temporal.Instant.compare(s.startInstant, startOfToday) >= 0);

  const prevUpcomingLen = useRef<number>(upcoming.length);
  const prevFuturesLen = useRef<number>(futures.length);

  useEffect(() => {
    if (prevUpcomingLen.current > 0 && upcoming.length === 0) {
      logError('student session view narrowed', {
        component: 'StudentHome',
        action: 'upcoming_empty',
        userId: actingUser?.id,
      });
    }
    prevUpcomingLen.current = upcoming.length;
  }, [upcoming.length, logError, actingUser?.id]);

  useEffect(() => {
    if (prevFuturesLen.current > 0 && futures.length === 0) {
      logError('student session view narrowed', {
        component: 'StudentHome',
        action: 'futures_empty',
        userId: actingUser?.id,
      });
    }
    prevFuturesLen.current = futures.length;
  }, [futures.length, logError, actingUser?.id]);

  if (isPending) return <LoadingPage />;
  if (error) {
    return (
      <div>We are sorry, but we could not fetch your next class. Please notify your teacher about this issue.</div>
    );
  }

  return (
    <div className="block p-4">
      <div className="flex items-center mb-4">
        <span className="flex items-center font-bold text-2xl my-2 mr-3">👋</span>
        <h2 className="flex items-center font-bold text-2xl my-2" data-testid="student-home-header-greeting">
          Hi {actingUser.firstName}!
        </h2>
      </div>
      <SessionSection
        title="Upcoming classes"
        studentName={actingUser.fullNameOrEmail}
        sessionsList={upcoming}
        data-testid="student-upcoming-classes-section"
      />
      {upcoming.length > 0 && (
        <SessionSection
          title="Future classes"
          studentName={actingUser.fullNameOrEmail}
          sessionsList={futures}
          isFuture
          data-testid="student-future-classes-section"
        />
      )}
    </div>
  );
};
